import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { getCountryCode } from '@cyberint/common-utils';
import { Flag } from '@cyberint/dsc';
import { t } from '@cyberint/stateful-ui/i18n';
import { EmptyStateMessage } from '@cyberint/stateful-ui/victimology/VictimologyTable/components/EmptyStateMessage';
import { FiltersContainer } from '@cyberint/stateful-ui/victimology/VictimologyTable/components/FiltersContainer';
import { RecentAttacksTable } from '@cyberint/stateful-ui/victimology/VictimologyTable/components/RecentAttacksTable';
import {
  filteredVictimologyState,
  victimologyFiltersState,
  victimologyState,
  victimologyTempFiltersState
} from './store/atoms';
import { COUNTRY, REGION, SECTOR } from './utils';
import * as S from './styled';

export const VictimologyTable = ({ victimology, onRowClick }) => {
  const victimologyData = useAtomValue(victimologyState) || [];
  const filteredData = useAtomValue(filteredVictimologyState);

  const setVictimologyData = useSetAtom(victimologyState);
  const setTempFilters = useSetAtom(victimologyTempFiltersState);

  const [filters, setFilters] = useAtom(victimologyFiltersState);

  const resetVictimologyFilters = useResetAtom(victimologyFiltersState);
  const resetVictimologyData = useResetAtom(victimologyState);
  const resetTempFilters = useResetAtom(victimologyTempFiltersState);

  useEffect(() => {
    setVictimologyData(victimology);
    return () => {
      resetVictimologyFilters();
      resetVictimologyData();
      resetTempFilters();
    };
  }, [victimology]);

  const onCardItemClick = (value, field) => () => {
    const updatedFilters = { ...filters, [field]: [...filters[field], value] };

    setFilters(updatedFilters);
    setTempFilters(updatedFilters);
  };

  if (!victimologyData.length || !filteredData) return null;

  const { regions, sectors, countries } = filteredData;

  const buildCard = (title, items, field) => (
    <S.Card>
      <S.CardTitle>{title}</S.CardTitle>
      <S.CardContent>
        {items?.length > 0 ? (
          items
            .toSorted((a, b) => b[1] - a[1])
            .map(([name, count]) => {
              const countryCode = field === COUNTRY && getCountryCode(name);
              return (
                <S.CardItemWrapper key={name} onClick={onCardItemClick(name, field)}>
                  {countryCode && <Flag alt={name} code={countryCode} size={22} />}
                  <S.CountAndName>
                    <S.CardItemName title={name}>{name}</S.CardItemName>
                    <S.AttacksCount>{count.toLocaleString()}</S.AttacksCount>
                  </S.CountAndName>
                </S.CardItemWrapper>
              );
            })
        ) : (
          <EmptyStateMessage />
        )}
      </S.CardContent>
    </S.Card>
  );

  return (
    <S.Container>
      <FiltersContainer />
      <S.SidebarAndTableWrapper>
        <S.Sidebar>
          {buildCard(t('victimology.attacked_regions'), regions, REGION)}
          {buildCard(t('victimology.attacked_countries'), countries, COUNTRY)}
          {buildCard(t('victimology.attacked_sectors'), sectors, SECTOR)}
        </S.Sidebar>
        <RecentAttacksTable onRowClick={onRowClick} />
      </S.SidebarAndTableWrapper>
    </S.Container>
  );
};

VictimologyTable.propTypes = {
  victimology: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onRowClick: PropTypes.func
};
